import { DeleteVideoDto } from "@repo/api/video";
import { AmplifilesClient } from "./api-client";
import {
  BackgroundMusicDto,
  UploadBackgroundMusicDto,
  ToggleBackgroundMusicFavoriteDto,
  GetBackgroundMusicsByExtIdsResponseDto,
  GetPagedBackgroundMusicsBodyDto,
  PagedBackgroundMusicsResponseDto,
  MusicMetadata,
  BeatSyncDto,
  CreateBeatSyncDto,
} from "@repo/api/background-music";

export class BackgroundMusicApi {
  static async getBackgroundMusicsByExtIds(
    teamExternalId: string,
    externalIds: string[],
  ): Promise<GetBackgroundMusicsByExtIdsResponseDto> {
    if (externalIds.length === 0) {
      return [];
    }

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music-by-ext-ids`,
      { externalIds },
    );

    return GetBackgroundMusicsByExtIdsResponseDto.parse(response.data);
  }

  static async getPagedBackgroundMusics(
    teamExternalId: string,
    body: GetPagedBackgroundMusicsBodyDto,
  ): Promise<PagedBackgroundMusicsResponseDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music-paged`,
      body,
    );

    return PagedBackgroundMusicsResponseDto.parse(response.data);
  }

  static async getBackgroundMusicMetadata(
    teamExternalId: string,
  ): Promise<MusicMetadata> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/background-music-metadata`,
    );

    return MusicMetadata.parse(response.data);
  }

  static async uploadBackgroundMusic(
    teamExternalId: string,
    values: UploadBackgroundMusicDto,
    track: File,
  ): Promise<BackgroundMusicDto> {
    const formData = new FormData();
    formData.append("name", values.name);

    formData.append("genres", values.genres);
    formData.append("moods", values.moods);
    formData.append("tags", values.tags);

    if (values.durationMs !== undefined) {
      formData.append("durationMs", String(values.durationMs));
    }

    if (values.peaks) {
      formData.append("peaks", values.peaks);
    }

    formData.append("audio", track);

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return BackgroundMusicDto.parse(response.data);
  }

  static async updateBackgroundMusic(
    teamExternalId: string,
    backgroundMusicExternalId: string,
    values: UploadBackgroundMusicDto,
    track: File | undefined,
  ): Promise<BackgroundMusicDto> {
    const formData = new FormData();
    formData.append("name", values.name);

    formData.append("genres", values.genres);
    formData.append("moods", values.moods);
    formData.append("tags", values.tags);

    if (values.durationMs !== undefined) {
      formData.append("durationMs", String(values.durationMs));
    }

    if (values.peaks) {
      formData.append("peaks", values.peaks);
    }

    if (track) {
      formData.append("audio", track);
    }

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music/${backgroundMusicExternalId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    return BackgroundMusicDto.parse(response.data);
  }

  static async deleteBackgroundMusic(
    teamExternalId: string,
    backgroundMusicExternalId: string,
  ): Promise<DeleteVideoDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music/${backgroundMusicExternalId}/delete`,
    );

    return DeleteVideoDto.parse(response.data);
  }

  static async toggleBackgroundMusicFavorite(
    teamExternalId: string,
    backgroundMusicExternalId: string,
  ): Promise<ToggleBackgroundMusicFavoriteDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music/${backgroundMusicExternalId}/favorite`,
    );

    return ToggleBackgroundMusicFavoriteDto.parse(response.data);
  }

  static async createBeatSync(
    teamExternalId: string,
    backgroundMusicExternalId: string,
    values: CreateBeatSyncDto,
  ): Promise<BeatSyncDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music/${backgroundMusicExternalId}/beat-sync`,
      values,
    );

    return BeatSyncDto.parse(response.data);
  }

  static async updateBeatSync(
    teamExternalId: string,
    backgroundMusicExternalId: string,
    beatSyncExternalId: string,
    values: CreateBeatSyncDto,
  ): Promise<BeatSyncDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music/${backgroundMusicExternalId}/beat-sync/${beatSyncExternalId}`,
      values,
    );

    return BeatSyncDto.parse(response.data);
  }

  static async getBeatSyncs(
    teamExternalId: string,
    backgroundMusicExternalId: string,
  ): Promise<BeatSyncDto[]> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/background-music/${backgroundMusicExternalId}/beat-sync`,
    );

    return BeatSyncDto.array().parse(response.data);
  }

  static async deleteBeatSync(
    teamExternalId: string,
    backgroundMusicExternalId: string,
    beatSyncExternalId: string,
  ): Promise<DeleteVideoDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/background-music/${backgroundMusicExternalId}/beat-sync/${beatSyncExternalId}/delete`,
    );

    return DeleteVideoDto.parse(response.data);
  }
}
